import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Avatar } from '../Avatar/Avatar';
import './CardProjects.css';

/**
 * Task card for the Projects view. Figma: "Card-Projects" (Model Creation
 * `3021:4329` regular, `3480:17756` draft).
 *
 * Layout: header row (task-type tile on the left, actions on the right),
 * title row with optional status tag, optional description line, optional
 * body tag (e.g. the Draft tag, below the description), detail rows
 * (`children`), and a bordered footer with the owner avatar and an optional
 * action button.
 */
export type CardProjectsProps = {
  title?: string;
  /** Top-left tile, typically a `TaskTypeIcon`. */
  icon?: ReactNode;
  /** Tag rendered below the title/description (e.g. the Draft tag). */
  bodyTag?: ReactNode;
  /** Top-right element, typically a dots trigger wrapped in a dropdown. */
  moreActions?: ReactNode;
  /** Element rendered next to the title (e.g. TagFeedback "Deployed (V5)"). */
  statusContent?: ReactNode;
  /** Small line under the title (e.g. draft "Steps missing: ..."). */
  description?: ReactNode;
  /** Detail rows (`.spark-card-projects-detail-row` items). */
  children?: ReactNode;
  footer?: boolean;
  /** Owner shown in the footer as a small avatar + name. */
  ownerName?: string;
  /** Owner profile image; when set the footer avatar shows it instead of initials. */
  ownerAvatarSrc?: string;
  /** Optional action button on the footer's right side. */
  footerAction?: ReactNode;
  /** Custom content replacing the default footer (owner + action). */
  footerContent?: ReactNode;
  className?: string;
  /**
   * Selected state for bulk actions (Figma `8001:3585`): light-blue fill and
   * a 1px brand border around the whole card, including the footer.
   */
  selected?: boolean;
  /** Card-level click handler; also adds `spark-card-projects-clickable` class. */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
};

export function CardProjects({
  title = 'Task name 2 V1',
  icon,
  bodyTag,
  moreActions,
  statusContent,
  description,
  children,
  footer = true,
  ownerName,
  ownerAvatarSrc,
  footerAction,
  footerContent,
  className = '',
  selected = false,
  onClick,
}: CardProjectsProps) {
  const hasHeader = Boolean(icon || moreActions);

  return (
    <article
      className={[
        'spark-card-projects',
        onClick && 'spark-card-projects-clickable',
        selected && 'spark-card-projects-selected',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-selected={selected || undefined}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event: KeyboardEvent<HTMLElement>) => {
              // Only activate for the card itself, not focused children.
              if (event.target !== event.currentTarget) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(event as unknown as MouseEvent<HTMLElement>);
              }
            }
          : undefined
      }
    >
      <div className="spark-card-projects-body">
        {hasHeader ? (
          <div className="spark-card-projects-header">
            <div className="spark-card-projects-header-left">
              {icon}
            </div>
            {moreActions ? (
              <div className="spark-card-projects-header-actions">{moreActions}</div>
            ) : null}
          </div>
        ) : null}
        <div className="spark-card-projects-heading">
          <div className="spark-card-projects-title-row">
            <h3 className="spark-card-projects-title">{title}</h3>
            {statusContent ? (
              <div className="spark-card-projects-status">{statusContent}</div>
            ) : null}
          </div>
          {description ? (
            <div className="spark-card-projects-description">{description}</div>
          ) : null}
        </div>
        {bodyTag ? <div className="spark-card-projects-body-tag">{bodyTag}</div> : null}
        {children ? <dl className="spark-card-projects-details">{children}</dl> : null}
      </div>
      {footer ? (
        <footer className="spark-card-projects-footer">
          {footerContent ?? (
            <>
              <div className="spark-card-projects-owner">
                <Avatar
                  size="small"
                  type={ownerAvatarSrc ? 'image' : 'initials'}
                  src={ownerAvatarSrc || undefined}
                  alt={ownerName || 'Owner avatar'}
                  primaryText={ownerName || ''}
                />
                <span className="spark-card-projects-owner-name">{ownerName}</span>
              </div>
              {footerAction ? (
                <div className="spark-card-projects-footer-action">{footerAction}</div>
              ) : null}
            </>
          )}
        </footer>
      ) : null}
    </article>
  );
}
